import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { LogoFull } from "./Logo";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const LINKS = [
  { label: "Riscos", href: "#riscos" },
  { label: "Simulador", href: "#simulador" },
  { label: "Soluções", href: "#solucoes" },
  { label: "Modalidades", href: "#modalidades" },
  { label: "Sobre", href: "#sobre" },
  { label: "Depoimentos", href: "#depoimentos" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navigation-bar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0B1320]/85 backdrop-blur-xl border-b border-amber-500/15 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a href="#inicio" data-testid="nav-logo-link" aria-label="TSS - ADVOGADOS, voltar ao início">
          <LogoFull compact={scrolled} />
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
              className="text-sm text-slate-300 hover:text-amber-300 transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <motion.a
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            href={waLink(WA_MESSAGES.hero)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 gold-surface text-[#0B1320] text-sm font-semibold px-5 py-2.5 rounded-full shadow-[0_0_24px_rgba(212,175,55,0.25)] hover:shadow-[0_0_36px_rgba(212,175,55,0.45)] transition-shadow duration-300"
          >
            <MessageCircle className="w-4 h-4" />
            Atendimento Confidencial
          </motion.a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Abrir menu"
            className="lg:hidden text-slate-200 p-2"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#0B1320]/95 backdrop-blur-xl border-b border-amber-500/15 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-slate-200 text-base py-1 hover:text-amber-300 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waLink(WA_MESSAGES.hero)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 gold-surface text-[#0B1320] font-semibold px-5 py-3 rounded-full mt-2"
              >
                <MessageCircle className="w-4 h-4" />
                Fale Conosco pelo WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
